import React, { useLayoutEffect, useRef } from "react";
import { gelatinFrames, GELATIN_DURATION, TabAxis, TabSpan } from "../utils/gelatinMotion";

// Preserve only a recent drawing position when workspace routes swap headers.
const continuity = new Map<string, { span: TabSpan; width: number; time: number }>();

export default function StepTab({ activeKey, selector, axis = "vertical", continuityKey }: {
  activeKey: string | number;
  selector: string;
  axis?: TabAxis;
  continuityKey?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const placed = useRef<TabSpan | null>(null);
  const flight = useRef<Animation | null>(null);

  useLayoutEffect(() => {
    const tab = ref.current;
    const parent = tab?.parentElement;
    if (!tab || !parent) return;
    const horizontal = axis === "horizontal";
    const position = horizontal ? "left" : "top";
    const dimension = horizontal ? "width" : "height";
    let parentWidth = parent.offsetWidth;
    let frames: Keyframe[] | null = null;
    const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const measure = (): TabSpan | null => {
      const target = parent.querySelector<HTMLElement>(selector);
      return target ? {
        start: horizontal ? target.offsetLeft : target.offsetTop,
        size: horizontal ? target.offsetWidth : target.offsetHeight,
      } : null;
    };
    const apply = (span: TabSpan | null) => {
      tab.style.opacity = span ? "1" : "0";
      if (!span) return;
      tab.style[position] = `${span.start}px`;
      tab.style[dimension] = `${span.size}px`;
      placed.current = span;
      if (parent.offsetWidth) parentWidth = parent.offsetWidth;
    };
    const stop = () => {
      flight.current?.cancel();
      flight.current = null;
    };
    const align = () => { stop(); apply(measure()); };
    const cached = continuityKey ? continuity.get(continuityKey) : undefined;
    const from = placed.current ?? (cached && performance.now() - cached.time < 1500 && Math.abs(cached.width - parent.offsetWidth) < 2 ? cached.span : null);
    const next = measure();
    apply(next);
    if (from && next && !motion?.matches && typeof tab.animate === "function" &&
      (Math.abs(from.start - next.start) > .5 || Math.abs(from.size - next.size) > .5)) {
      frames = gelatinFrames(from, next, axis);
      const animation = tab.animate(frames, { duration: GELATIN_DURATION, easing: "linear" });
      flight.current = animation;
      animation.onfinish = () => { if (flight.current === animation) { flight.current = null; apply(measure()); } };
    }

    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => {
      const target = measure();
      // ResizeObserver also delivers an initial observation during a flight.
      if (!flight.current || !target || !placed.current || Math.abs(target.start - placed.current.start) > .5 || Math.abs(target.size - placed.current.size) > .5) align();
    }) : null;
    observer?.observe(parent);
    const target = parent.querySelector<HTMLElement>(selector);
    if (target) observer?.observe(target);
    motion?.addEventListener?.("change", align);
    window.addEventListener("resize", align);
    return () => {
      observer?.disconnect();
      motion?.removeEventListener?.("change", align);
      window.removeEventListener("resize", align);
      let drawn = placed.current;
      const time = flight.current?.currentTime;
      if (frames && typeof time === "number") {
        // A route can detach this header before child effects clean up, so
        // sample the animation clock instead of relying on detached geometry.
        const progress = Math.min(1, Math.max(0, time / GELATIN_DURATION)) * (frames.length - 1);
        const index = Math.min(frames.length - 2, Math.floor(progress));
        const mix = (property: "left" | "top" | "width" | "height") => {
          const a = parseFloat(frames![index][property] as string);
          const b = parseFloat(frames![index + 1][property] as string);
          return a + (b - a) * (progress - index);
        };
        drawn = { start: mix(position), size: mix(dimension) };
      } else if (tab.isConnected) {
        const style = getComputedStyle(tab);
        drawn = { start: parseFloat(style[position]), size: parseFloat(style[dimension]) };
      }
      stop();
      if (drawn && Number.isFinite(drawn.start) && Number.isFinite(drawn.size)) {
        apply(drawn);
        if (continuityKey) continuity.set(continuityKey, { span: drawn, width: parentWidth, time: performance.now() });
      }
    };
  }, [activeKey, selector, axis, continuityKey]);

  return <span ref={ref} className={`step-tab${axis === "horizontal" ? " step-tab-horizontal" : ""}`} aria-hidden="true" style={{ opacity: 0 }} />;
}
